import { BehaviorSubject } from "rxjs";

import { Destructable, Model } from "@models";
import { InitService } from "@core-services/init.service";
import { debug } from "@helpers/debug";

import { DestructorService } from "@core-services/destructor.service";
export abstract class Service<M extends Model> implements Destructable {
	protected _list: BehaviorSubject<M[]> = new BehaviorSubject<M[]>([]);

	public destroy(): void {
		this._list.next([]);
		this._list.complete();
	}

	protected constructor(
		protected initService: InitService,
		protected destructor: DestructorService,
		public name: string
	) {}

	public updateItem(m: M, id: string = m.getId()): void {
		this.log({ ...this.list.find((item) => item.getId() === id) }, m, "update_item");
		this.list = this.list.map((item) => (item.getId() === id ? m : item));
	}

	public updateItems(items: M[]): void {
		this.list = this.list.map((m) => {
			const updatedItem = items.find((item) => item.getId() === m.getId());
			return updatedItem ?? m;
		});
	}

	public upsert(m: M): void {
		this.hasItem(m) ? this.updateItem(m) : this.addItem(m);
	}

	public hasItem(m: M): boolean {
		return this.list.some((item) => item.getId() === m.getId());
	}

	public addItem(m: M): void {
		this.log(this.list, m, "add_item");
		this.list = [...this.list, m];
	}

	public addItems(items: M[]): void {
		const newItems = items.filter((item) => this.list.every((m) => !m.getId().includes(item.getId())));
		this.list = [...this.list, ...newItems];
	}

	public getItemById(id: string): M {
		return this.list.find((m) => (!m ? false : m.getId() === id));
	}

	public getList(): BehaviorSubject<M[]> {
		return this._list;
	}

	public get list(): M[] {
		return this._list.getValue().filter((i: M) => !!i);
	}

	public set list(m: M[]) {
		this.log([...this.list], m, "update_list");
		this.initService.init(this.name);
		this._list.next(m);
	}

	public deleteItem(m: M): void {
		this.log(this.list, m, "delete_item");
		this.list = [...this.list.filter((i) => m.getId() !== i.getId())];
	}

	/**
	 * Needed for logging only
	 */
	private lastTimeout: ReturnType<typeof setTimeout> = null;
	private debounceCount: number = 0;
	protected logDebounceTime: number = 50;

	// TODO: eslint fix
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	protected log(previous: any, next: any, action: string): void {
		if (!debug) {
			return;
		}

		if (this.lastTimeout != null) {
			clearTimeout(this.lastTimeout);
			this.debounceCount++;
		}

		this.lastTimeout = setTimeout(() => {
			const grey: string = "color:grey";
			const blue: string = "color:#63c1ff";
			const green: string = "color:#96fa6b";
			const orange: string = "color:#ffac1c";
			const _default: string = "";

			console.group(
				"[" + this.name + "] %c%s",
				grey,
				new Date().toISOString().split("T").join(" ").split("Z")[0]
			);
			console.log("%cprevious \t%c%O", grey, _default, previous);
			console.log("%caction \t\t%c%s", blue, _default, action);
			console.log("%cnext\t\t%c%O", green, _default, next);
			if (this.debounceCount > 0) {
				console.log("%cdebounced \t%c%s", orange, orange, this.debounceCount + "x");
			}
			console.groupEnd();

			this.debounceCount = 0;
		}, this.logDebounceTime);
	}
}
